import React, { type Ref, useMemo } from 'react';
import WarningIcon from '@atlaskit/icon/core/status-warning';
import { xcss, Pressable, Box } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import { useIntl } from '@atlassian/jira-intl';
import {
	BANNER,
	ContextualAnalyticsData,
	FireScreenAnalytics,
	fireUIAnalytics,
	type UIAnalyticsEvent,
} from '@atlassian/jira-product-analytics-bridge';
import {
	useJsmGracePeriodAnnouncementDisplayStateResource,
	useJsmGracePeriodAnnouncementGpEndDateResource,
} from '@atlassian/jira-router-resources-jsm-grace-period-announcements/src/services/index.tsx';
import { FALLBACK_GP_END_DATE } from '@atlassian/jira-router-resources-jsm-grace-period-announcements/src/services/constants.tsx';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { premiumLandingPageEntryPoint } from '@atlassian/jira-servicedesk-premium-landing-page/entrypoint.tsx';
import { Trigger, usePanel } from '@atlassian/entry-point-panel';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller/src/components/app-editions/index.tsx';
import { STANDARD_EDITION } from '@atlassian/jira-shared-types/src/edition.tsx';
import { useIsSiteAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-site-admin/index.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { useProductAndEdition } from '@atlassian/jira-edition-awareness-for-standard-to-premium/src/services/use-product-and-edition/index.tsx';
import { useProductEntitlementDetails } from '@atlassian/jira-tenant-context-controller/src/components/product-entitlement-details/index.tsx';
import { SERVICE_DESK } from '@atlassian/jira-shared-types/src/application-key.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { daysLeftCalculation } from '../../controllers/utils/index.tsx';
import type { GpAwarenessPillProps } from './types.tsx';
import messages from './messages.tsx';

export function GpAwarenessPill({ analyticsOrigin }: GpAwarenessPillProps): React.JSX.Element {
	return (
		<UFOSegment name="gp-awareness-pill-top-nav">
			<JSErrorBoundary
				id="jsm-grace-period-banner.ui.gp-awareness-pill"
				packageName="jsm-grace-period-awareness-pill"
				teamName="Sequoia"
				fallback="unmount"
			>
				<GpAwarenessPillInternal analyticsOrigin={analyticsOrigin} />
			</JSErrorBoundary>
		</UFOSegment>
	);
}

export const GpAwarenessPillInternal = ({ analyticsOrigin }: GpAwarenessPillProps) => {
	const { formatMessage } = useIntl();
	const accountId = useAccountId();
	const isSiteAdmin = useIsSiteAdmin();
	const appEditions = useAppEditions();
	const { product } = useProductAndEdition();
	const { gpEndDate, loading: gpEndDateLoading } = useJsmGracePeriodAnnouncementGpEndDateResource();
	const { displayState: bannerDisplayState, loading: bannerDisplayStateLoading } =
		useJsmGracePeriodAnnouncementDisplayStateResource(accountId);
	const { Panel, trigger } = usePanel();
	const productEntitlementDetails = useProductEntitlementDetails();
	const isCCP = productEntitlementDetails?.[SERVICE_DESK]?.billingSourceSystem === 'CCP';

	const daysLeft = useMemo(() => daysLeftCalculation(FALLBACK_GP_END_DATE, gpEndDate), [gpEndDate]);

	const getPillDetails = () => {
		if (daysLeft <= 7 && fg('jsm_repackaging_t7_days_nudge_in_product')) {
			return { announcementType: 'T7', fallbackDaysLeft: 7 };
		}

		if (daysLeft <= 30) {
			return { announcementType: 'T30', fallbackDaysLeft: 30 };
		}

		return { announcementType: 'T90', fallbackDaysLeft: 90 };
	};

	const isGPFinished = daysLeft <= 0;
	const { announcementType } = getPillDetails();
	// We accidentally released the new T90 state for all customers
	// even if they had more than 90 days left in their GP.
	// Some of these customers might have already dismissed the banner
	// so we need to use the new state for the banner if their GP end date is after Nov 5, 2025
	// to for storing their T90 banner display state
	const useNewT90State =
		gpEndDate &&
		gpEndDate > new Date('11/5/2025') &&
		fg('jsm_use_new_t90_state_jsm_gp_announcements');

	const handleOnClick = (
		_: React.MouseEvent<HTMLElement, MouseEvent>,
		analyticsEvent: UIAnalyticsEvent,
	) => {
		if (analyticsOrigin) {
			fireUIAnalytics(analyticsEvent, 'jsmGracePeriodPill', {
				origin: analyticsOrigin,
			});
		}
	};

	const t90BannerIsVisible =
		(announcementType === 'T90' && !bannerDisplayState?.hideT90Banner && !useNewT90State) ||
		(announcementType === 'T90' && !bannerDisplayState?.hideT90BannerNew && useNewT90State);
	const t30BannerIsVisible = announcementType === 'T30' && !bannerDisplayState?.hideT30Banner;

	if (
		!isSiteAdmin ||
		appEditions.serviceDesk !== STANDARD_EDITION ||
		gpEndDateLoading ||
		bannerDisplayStateLoading ||
		t90BannerIsVisible ||
		(!fg('jsm_show_gp_awareness_pill_t30') && t30BannerIsVisible) ||
		product !== 'jira-service-management' ||
		isGPFinished ||
		!isCCP ||
		daysLeft > 90
	) {
		return;
	}

	return (
		// This onClick handler is needed to prevent a click event on the panel from bubbling up to the parent and closing the panel
		// eslint-disable-next-line @atlassian/a11y/click-events-have-key-events, @atlassian/a11y/interactive-element-not-keyboard-focusable, @atlassian/a11y/no-static-element-interactions
		<div
			onClick={(event: React.MouseEvent<HTMLElement, MouseEvent>) => event.stopPropagation()}
			// eslint-disable-next-line jira/react/no-style-attribute, @atlaskit/ui-styling-standard/enforce-style-prop
			style={{ width: 'max-content' }}
		>
			{fg('jsm_gp_intercom_fix') && (
				<Box xcss={panelBoxStyles}>
					<Panel />
				</Box>
			)}
			<ContextualAnalyticsData
				sourceName="jsmGracePeriodPill"
				sourceType={BANNER}
				attributes={{ daysLeftInGP: daysLeft }}
			>
				<FireScreenAnalytics />
				<Trigger
					trigger={trigger}
					entryPoint={premiumLandingPageEntryPoint}
					entryPointProps={{
						origin: 'NAV_BAR',
						onClose: () => {
							trigger.setPanel(null);
						},
					}}
				>
					{({ ref }) => (
						<Pressable
							onClick={handleOnClick}
							xcss={[
								pressableStyles,
								announcementType === 'T30' && warningStyles,
								announcementType === 'T7' && dangerStyles,
							]}
							// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
							ref={ref as Ref<HTMLButtonElement>}
							testId="jsm-grace-period-banner.ui.gp-awareness-pill.button"
						>
							{announcementType === 'T7' ? (
								<StatusErrorIcon
									label=""
									color={token('color.icon.danger')}
									testId="jsm-grace-period-banner.ui.gp-awareness-pill.status-error-icon"
								/>
							) : (
								<WarningIcon
									label=""
									color={token('color.icon.warning')}
									testId="jsm-grace-period-banner.ui.gp-awareness-pill.status-warning-icon"
								/>
							)}
							{gpEndDate
								? formatMessage(messages.daysLeft, {
										days: daysLeft,
										symbol: '',
									})
								: formatMessage(messages.daysLeftDefault)}
						</Pressable>
					)}
				</Trigger>
			</ContextualAnalyticsData>
			{!fg('jsm_gp_intercom_fix') && <Panel />}
		</div>
	);
};

const pressableStyles = xcss({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
	textDecoration: 'none !important',
	backgroundColor: 'color.background.neutral.subtle',
	display: 'inline-flex',
	border: `1px solid ${token('color.border.warning')}`,
	borderRadius: 'radius.small',
	color: 'color.text.warning',
	flexShrink: 0,
	height: '2rem',
	font: 'font.body',
	fontWeight: 'font.weight.medium',
	paddingBlock: 'space.075',
	paddingInlineEnd: 'space.150',
	paddingInlineStart: 'space.150',
	textAlign: 'center',
	transition: 'background 0.1s ease-out',
	alignItems: 'center',
	gap: 'space.075',
	':hover': {
		backgroundColor: 'color.background.neutral.subtle.hovered',
	},
	':active': {
		backgroundColor: 'color.background.neutral.subtle.pressed',
	},
});

const warningStyles = xcss({
	backgroundColor: 'color.background.warning',

	':hover': {
		backgroundColor: 'color.background.warning.hovered',
	},
	':active': {
		backgroundColor: 'color.background.warning.pressed',
	},
});

const dangerStyles = xcss({
	backgroundColor: 'color.background.danger',
	borderColor: 'color.border.danger',
	color: 'color.text.danger',

	':hover': {
		backgroundColor: 'color.background.danger.hovered',
	},
	':active': {
		backgroundColor: 'color.background.danger.pressed',
	},
});

const panelBoxStyles = xcss({
	position: 'absolute',
	top: '0',
	left: '0',
	width: '1px',
	height: '1px',
});

export default GpAwarenessPill;
